import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Search } from "lucide-react";

import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useNotification } from "../../../contexts/NotificationContext";
import { usePricing } from "../../../contexts/PricingContext";
import { placeDependencies } from "../../pricing/repository";
import { normalizeText } from "../../../lib/pricing";
import { PRICING_PATH } from "../../forms/ZoneForm";
import { Item } from "../settings/Item";
import { inputClass } from "./fields";
import ListRow from "./ListRow";
import { useDeleteItem } from "./useDeleteItem";

const ALL = "__all__";

export default function PlacesTab() {
  const { places, zones, zoneNames, data } = usePricing();
  const { showNotification } = useNotification();
  const [zoneFilter, setZoneFilter] = useState(ALL);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { requestDelete, dialog } = useDeleteItem("places", "Local");

  const handleDelete = (id: string, name: string) => {
    const deps = placeDependencies(data, id);
    if (deps.blocked) {
      showNotification(
        "warning",
        `"${name}" é usado em ${deps.routes} tarifa(s). Remova-as ou desative o local.`
      );
      return;
    }
    requestDelete(id, name);
  };

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    return places.filter((place) => {
      if (zoneFilter !== ALL && place.zoneId !== zoneFilter) return false;
      if (!q) return true;
      return [place.name, ...place.aliases].some((k) => normalizeText(k).includes(q));
    });
  }, [places, zoneFilter, search]);

  return (
    <Item className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar local"
            aria-label="Buscar local"
            className="h-10 w-full min-w-0 rounded-md border border-input bg-white/5 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </label>
        <Select value={zoneFilter} onValueChange={setZoneFilter}>
          <SelectTrigger className={inputClass} aria-label="Filtrar por região">
            <SelectValue placeholder="Todas as regiões" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as regiões</SelectItem>
            {zones.map((zone) => (
              <SelectItem key={zone.id} value={zone.id}>
                {zone.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-white/60">
          {places.length === 0 ? "Nenhum local cadastrado." : "Nenhum local encontrado."}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((place) => (
            <ListRow
              key={place.id}
              title={place.name}
              subtitle={zoneNames[place.zoneId] || "Região removida"}
              active={place.active}
              meta={
                place.aliases.length > 0 ? (
                  <span className="truncate text-[11px] text-white/40">
                    {place.aliases.join(" · ")}
                  </span>
                ) : undefined
              }
              onEdit={() => navigate(`${PRICING_PATH}/places/edit/${place.id}`)}
              onDelete={() => handleDelete(place.id, place.name)}
              deleteTitle={
                placeDependencies(data, place.id).blocked
                  ? "Remova as tarifas que usam este local antes de excluir"
                  : undefined
              }
            />
          ))}
        </div>
      )}
      <Button
        variant="outline"
        className="w-full"
        disabled={zones.length === 0}
        onClick={() => navigate(`${PRICING_PATH}/places/add`)}
      >
        <Plus className="h-4 w-4" /> Adicionar local
      </Button>
      {dialog}
    </Item>
  );
}
